import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import axios from 'axios';
import {
  CheckCircle,
  XCircle,
  Loader2,
  Home,
  BookOpen,
  Crown,
  Heart
} from 'lucide-react';

// Import API URL from utility
import { API_URL } from '../lib/api';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth, getAuthHeaders } = useAuth();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (pollCount >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/api/payments/status/${sessionId}`,
        { headers: getAuthHeaders(), withCredentials: true }
      );

      setPaymentInfo(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Refresh user data to get updated tier
        await checkAuth();
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setPollCount(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-emerald-100 rounded-full flex items-center justify-center">
              <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Processing Payment...</h1>
            <p className="text-slate-600">Please wait while we confirm your payment.</p>
          </div>
        );

      case 'success':
        const isMembership = paymentInfo?.transaction_type?.includes('membership');
        const isDonation = paymentInfo?.transaction_type === 'donation';
        
        return (
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-emerald-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-emerald-500" />
            </div>
            <h1 className="text-3xl font-serif font-bold text-slate-800 mb-2">
              {isDonation ? 'Thank You!' : 'Welcome Aboard!'}
            </h1>
            <p className="text-lg text-slate-600 mb-6">
              {isDonation 
                ? 'Your generous donation will help us nurture faith in children everywhere.'
                : `You're now a ${paymentInfo?.transaction_type?.includes('premium') ? 'Champion' : 'Adventurer'}!`
              }
            </p>
            
            <div className="bg-emerald-50 rounded-2xl p-6 mb-8 inline-block">
              <p className="text-sm text-slate-500 mb-1">Amount Paid</p>
              <p className="text-3xl font-bold text-emerald-600">
                £{paymentInfo?.amount?.toFixed(2)}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isMembership ? (
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-lg"
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  Start Reading
                </Button>
              ) : (
                <Button
                  onClick={() => navigate('/')}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-lg"
                >
                  <Home className="w-5 h-5 mr-2" />
                  Back to Home
                </Button>
              )}
            </div>
          </div>
        );

      case 'expired':
      case 'error':
      case 'timeout':
        return (
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">
              {status === 'expired' ? 'Payment Expired' : 'Something Went Wrong'}
            </h1>
            <p className="text-slate-600 mb-6">
              {status === 'expired'
                ? 'Your payment session has expired. Please try again.'
                : 'We couldn\'t verify your payment. Please check your email for confirmation.'
              }
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/upgrade')}
                variant="outline"
                className="rounded-full px-6"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/')}
                className="bg-slate-800 hover:bg-slate-900 text-white rounded-full px-6"
              >
                <Home className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-sky-50 to-amber-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 lg:p-12">
        {renderContent()}
      </div>
    </div>
  );
};

const PaymentCancelPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-sky-50 to-amber-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 lg:p-12 text-center">
        <div className="w-24 h-24 mx-auto mb-6 bg-slate-100 rounded-full flex items-center justify-center">
          <XCircle className="w-12 h-12 text-slate-400" />
        </div>
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Payment Cancelled</h1>
        <p className="text-slate-600 mb-6">
          No worries! You can try again whenever you're ready.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => navigate('/upgrade')}
            className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6"
          >
            View Plans
          </Button>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="rounded-full px-6"
          >
            Go Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export { PaymentSuccessPage, PaymentCancelPage };
export default PaymentSuccessPage;
